"use client";

import { Dispatch, SetStateAction, useEffect } from "react";
import { UserRoles } from "@/enums/user";
import type { AuthSeedResult, AuthzData, PermissionsData } from "@/lib/auth-seed";
import { ProductRolesState, areProductRolesStatesEqual } from "@/utils/common/auth";
import { sameStringSet } from "@/utils/authz/roleLabel";

function samePermissionsData(a: PermissionsData, b: PermissionsData): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.hasOwn(b, key) && a[key] === b[key]);
}

function sameAuthzData(a: AuthzData, b: AuthzData): boolean {
  if (a === b) return true;
  return (
    a.navContainer === b.navContainer &&
    a.isOrgGailGptV1 === b.isOrgGailGptV1 &&
    a.productsAllPausedOrCanceled === b.productsAllPausedOrCanceled &&
    a.isAdminWithNoRoles === b.isAdminWithNoRoles
  );
}

type AuthSeedSyncSetters = {
  setPermissions: Dispatch<SetStateAction<PermissionsData>>;
  setScopes: Dispatch<SetStateAction<string[]>>;
  setAuthz: Dispatch<SetStateAction<AuthzData>>;
  setScopesAuthoritative: Dispatch<SetStateAction<boolean>>;
  setProvisioningPending: Dispatch<SetStateAction<boolean>>;
  setPermissionsDegraded: Dispatch<SetStateAction<boolean>>;
  setRole: Dispatch<SetStateAction<UserRoles>>;
  setRawRoles: Dispatch<SetStateAction<string[]>>;
  setProductRolesState: Dispatch<SetStateAction<ProductRolesState>>;
};

/**
 * After `router.refresh()`, the force-dynamic layout re-runs `buildAuthSeed()` and
 * passes a new prop. Adopt Galactus-backed permission, role, and product-seat fields.
 */
export function useAuthSeedSync(
  authSeed: AuthSeedResult,
  setters: AuthSeedSyncSetters,
  suspensionPending: boolean,
): void {
  const {
    setPermissions,
    setScopes,
    setAuthz,
    setScopesAuthoritative,
    setProvisioningPending,
    setPermissionsDegraded,
    setRole,
    setRawRoles,
    setProductRolesState,
  } = setters;

  useEffect(() => {
    // Keep the mounted operation's shell intact until its redirect hold ends.
    // Server admission independently rejects new work while it is suspended.
    if (!authSeed.isAuthenticated || suspensionPending) return;

    setPermissions((current) =>
      samePermissionsData(current, authSeed.permissions) ? current : authSeed.permissions
    );
    setScopes((current) => (sameStringSet(current, authSeed.scopes) ? current : authSeed.scopes));
    setAuthz((current) => (sameAuthzData(current, authSeed.authz) ? current : authSeed.authz));
    setScopesAuthoritative((current) =>
      current === authSeed.scopesAuthoritative ? current : authSeed.scopesAuthoritative
    );
    setProvisioningPending((current) =>
      current === authSeed.provisioningPending ? current : authSeed.provisioningPending
    );
    setPermissionsDegraded((current) =>
      current === authSeed.permissionsDegraded ? current : authSeed.permissionsDegraded
    );
    setRole((current) => (current === authSeed.role ? current : authSeed.role));
    setRawRoles((current) =>
      sameStringSet(current, authSeed.rawRoles) ? current : authSeed.rawRoles
    );
    setProductRolesState((current) => {
      const next: ProductRolesState = { roles: authSeed.productRoles, loaded: true };
      return areProductRolesStatesEqual(current, next) ? current : next;
    });
  }, [
    authSeed,
    suspensionPending,
    setPermissions,
    setScopes,
    setAuthz,
    setScopesAuthoritative,
    setProvisioningPending,
    setPermissionsDegraded,
    setRole,
    setRawRoles,
    setProductRolesState,
  ]);
}
